import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, memo } from 'react'
import store, { RootState } from '../../store'
import { Box, Button, IconButton, Menu, MenuItem, Paper, ToggleButton, Tooltip } from '@mui/material'
import WatchListTopBar from './WatchlistTopBar'
import WatchListData from './WatchlistData'
import { useDispatch, useSelector } from 'react-redux'
import { updateWatchlist as updateWatchlistFromUtils } from './WatchUtils'
import { Security } from '../../proto/generated/security'
import { ReorderWatchlistRequest, UserWatchlists, Watchlist_ShareType, Watchlist_Type, Watchlist_WatchlistItem, Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist'
import commonActions from '../../actions/common.actions'
import websocketActions from '../../actions/websocket.actions'
import { styled } from '@mui/material/styles'
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup'
import { subscriptionTags } from '../../assets/constants'
import {
    changeWatchlistSecurities,
    createWatchlist,
    deleteWatchlist,
    fetchWatchlists,
    renameWatchlist,
    reorderWatchlistItem,
    reorderWatchlist,
    fetchCommunityWatchList,
    getWatchlist,
    addCommunityToWatchList
} from '../../services/apis/watchlist'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { getExchangeAndCodeFromKey, getSecurityKey } from '../../util/security'
import { clearPreviewWatchlist, setWatchlists, Watchlist, WatchlistItemData } from '../../reducer/watchList.reducer'
import { selectPreviewWatchlist, selectPreviewWatchlistId, selectWatchlists } from '../../selectors/watchList'
import { makeStyles } from 'tss-react/mui'
import { parseErrorFromArrayBuffer } from '../common/errorParser'
import { ErrorCodes } from '../../proto/generated/common'
import AddIcon from '../../assets/icons/AddIcon'
import SearchIcon from '../../assets/icons/SearchIcon'
import WatchlistSearchDialog from './WatchListSearchDialog'
import { v4 as uuid } from 'uuid'
import { deepClone } from '@mui/x-data-grid/utils/utils'
import LockIcon from '../../assets/icons/lockIcon'
import { scrollButtonStyles } from '../../pages/home/stockBar/stockBar'
import ThinArrowLeftIcon from '../../assets/icons/ThinArrowLeftIcon'
import ThinArrowRightIcon from '../../assets/icons/ThinArrowRightIcon'
import RenameWatchlistDailog from '../common/RenameWatchlistDailog'
import { WebSocketContext } from '../webSocketConnection/WebSocket.tsx'
import { WebSocketResponseMessage } from '../../proto/generated/websocket.ts'
import PlusIcon from '../../assets/icons/PlusIcon.tsx'
import { ClearIcon } from '@mui/x-date-pickers'
import { useUser } from '../../context/UserContext.tsx'
import { FeatureCategory, FeatureName } from '../adminUserRestrictions/Restrictions.constants.ts'
import { selectUserDetails } from '../../selectors/user.ts'
import { isAdmin } from '../../util/authentication.ts'
import { setFeatureRestrictionDialog } from '../../reducer/user.reducer.ts'
import { FeatureCode } from '../../util/RestrictionFeatureCode.ts'
import SubtleShimmer from '../common/SubtleShimmer'
import WatchlistSkeleton from './WatchlistSkeleton'
import { watchListView } from '../../selectors/user'

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        borderRadius: '4px 4px 0 0',
        boxShadow: '2px 0 2px 0 rgba(0, 0, 0, 0.06)'
    }
}))

const useStyles = makeStyles()((theme) => ({
    paperRoot: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        borderTopRightRadius: '6px'
    },
    blurred: {
        filter: 'blur(4px)'
    },
    clear: {
        filter: 'blur(0)'
    }
}))

function Watchlists() {
    const { classes } = useStyles()
    const ws = useContext(WebSocketContext)
    const selectedWatchlistId = useSelector((state: RootState) => state.commonStore.watchlistId)
    const selectedWatchlistIdRef = useRef<string>(selectedWatchlistId)
    const watchlists = useSelector(selectWatchlists)
    const selectedWatchlist = useMemo(() => watchlists[selectedWatchlistId], [watchlists, selectedWatchlistId])
    const dataUpdating = useRef<boolean>(false)
    const dispatch = useDispatch()
    const rectCache = useRef<DOMRect | null>(null)
    const addOrRemoveWatchlistDataItemData = useSelector((state: RootState) => state.watchListStore.addOrRemoveWatchlistItem)
    const [loading, setLoading] = useState<boolean>(true)
    const hasInitialLoaded = useRef(false)
    const tableView = useSelector(watchListView)
    const [securitiesModalOpen, setSecuritiesModalOpen] = useState<boolean>(false)
    const [showBottomBar, setShowBottomBar] = useState<boolean>(false)
    const previewId = useSelector(selectPreviewWatchlistId)
    const previewWatchlist = useSelector(selectPreviewWatchlist)
    const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<null | HTMLElement>(null)
    const [contextWatchlistId, setContextWatchlistId] = useState<string | null>(null)
    const [editMode, setEditMode] = useState<boolean>(false)
    const [isSearchDialogOpen, setSearchDialogOpen] = useState(false)
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
    const [draggedOverItemIndex, setDraggedOverItemIndex] = useState<number | null>(null)
    const lastfetchedSnapshots = useRef<string>('')
    const scrollDivRef = useRef<HTMLDivElement>(null)
    const [renameDialogOpen, setRenameDialogOpen] = useState(false)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [currentRenameWatchlist, setCurrentRenameWatchlist] = useState<{ id: string; name: string } | null>(null)
    const userWatchlists = useMemo(() => Object.values(watchlists).filter((wl) => wl?.shareType === Watchlist_ShareType.USER || wl?.shareType === Watchlist_ShareType.COMMUNITY), [watchlists])
    const currentSubscribedWatchlistRef = useRef<string | null>(null)
    const { user : {hasAccess}} = useUser()
    const communityWatchlistAccess = useMemo(()=>{
        return hasAccess(FeatureCategory.WATCHLIST , '' , FeatureName.COMMUNITY_WATCHLIST_ACCESS) 
    },[hasAccess])
    const user = useSelector(selectUserDetails)
    const isUserAdmin = useMemo(()=>{
        return isAdmin(user?.currentUser)
    },[user?.currentUser])

    const onWebSocketMessageReceived = useCallback(
        (data: Uint8Array) => {
            try {
                const responseData = WebSocketResponseMessage.decode(data)

                const watchlistResponse = responseData.watchlistSubscriptionResponses.find((res) => res.watchlists && res.watchlists.length > 0)

                if (!watchlistResponse) return

                const currentState = store.getState().watchListStore.watchlists
                const mergedWatchlists = { ...currentState }

                for (const protoWatchlist of watchlistResponse.watchlists) {
                    // Community check (assuming type 3 = community)
                    const isCommunity = protoWatchlist.shareType === Watchlist_ShareType.COMMUNITY

                    // Try to find the matching key in store
                    let storeKey: string | undefined

                    if (isCommunity) {
                        // Match by shareId
                        storeKey = Object.keys(currentState).find((id) => currentState[id].shareId === protoWatchlist.shareId)
                    } else {
                        // Match by id
                        if (currentState[protoWatchlist.id]) {
                            storeKey = protoWatchlist.id
                        }
                    }

                    // 🚨 If no match found → skip (don’t create new)
                    if (!storeKey) {
                        console.log('Skipping update, no matching watchlist in store for:', protoWatchlist.name)
                        continue
                    }

                    // Process items
                    const updatedItems = protoWatchlist.watchlistItems.map((item) => ({
                        id: `${item.security.exchange === 1 ? 'NSE' : 'BSE'}:${item.security.code}`,
                        item
                    }))

                    // Update only the matched watchlist
                    mergedWatchlists[storeKey] = {
                        ...protoWatchlist,
                        id: storeKey, // keep store id consistent
                        type: Array.isArray(protoWatchlist.type) ? protoWatchlist.type : [protoWatchlist.type],
                        watchlistItems: updatedItems
                    }
                }

                dispatch(setWatchlists(mergedWatchlists))
            } catch (e) {
                console.error('Error processing watchlist subscription response:', e)
            }
        },
        [dispatch]
    )

    useEffect(() => {
        ws!.addListener(onWebSocketMessageReceived)
        return () => {
            if (ws !== null) {
                ws.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [onWebSocketMessageReceived, ws])

    const updateScrollButtons = useCallback(() => {
        const el = scrollDivRef.current
        if (!el) return
        const { scrollLeft, scrollWidth, clientWidth } = el

        setCanScrollLeft(scrollLeft > 0)
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
    }, [])

    const scrollLeft = () => {
        if (scrollDivRef.current) {
            scrollDivRef.current.scrollBy({ left: -200, behavior: 'smooth' })
            setTimeout(updateScrollButtons, 100)
        }
    }

    const scrollRight = () => {
        if (scrollDivRef.current) {
            scrollDivRef.current.scrollBy({ left: 200, behavior: 'smooth' })
            setTimeout(updateScrollButtons, 100)
        }
    }

    useEffect(() => {
        const el = scrollDivRef.current
        if (!el) return
        requestAnimationFrame(() => updateScrollButtons())

        el.addEventListener('scroll', updateScrollButtons)
        window.addEventListener('resize', updateScrollButtons)

        return () => {
            el.removeEventListener('scroll', updateScrollButtons)
            window.removeEventListener('resize', updateScrollButtons)
        }
    }, [updateScrollButtons])

    const handleDragStart = (index: number) => {
        setDraggedIndex(index)
    }
    const handleDragOver = useCallback((e: React.DragEvent<HTMLLIElement>, index: number | null) => {
        e.preventDefault()
        setDraggedOverItemIndex(index)
    }, [])

    useEffect(() => {
        selectedWatchlistIdRef.current = selectedWatchlistId
    }, [selectedWatchlistId])

    const handleRightClick = (event: React.MouseEvent<HTMLElement>, buttonNumber: string) => {
        event.preventDefault()
        setContextMenuAnchorEl(event.currentTarget)
        setContextWatchlistId(buttonNumber)
    }

    const handleCloseContextMenu = () => {
        setContextMenuAnchorEl(null)
        setContextWatchlistId(null)
    }

    const updateWatchlist = useCallback(
        async (setDefault?: boolean, fetchSelected?: boolean, fetchList = false) => {
            dataUpdating.current = true
            let response: UserWatchlists = {
                watchlists: []
            }
            let data: UserWatchlists
            setLoading(true)
            if (fetchList || !fetchSelected) {
                response = await fetchWatchlists({ dispatch: dispatch })
                data = deepClone(response)
                const validWatchlists = data.watchlists.filter((w) => !(Array.isArray(w.type) ? w.type.includes(Watchlist_Type.REFERENCE) : w.type === Watchlist_Type.REFERENCE) || w.shareId)
                if (!validWatchlists.some((w) => w.id === selectedWatchlistIdRef.current)) {
                    selectedWatchlistIdRef.current = data.watchlists.find((w) => w.shareType === Watchlist_ShareType.USER || (w.type === Watchlist_Type.REFERENCE && w.shareId))?.id
                }
            } else if (fetchSelected) {
                data = deepClone({ watchlists: Object.values(watchlists) })
            } else {
                dataUpdating.current = false
                setLoading(false)
                return
            }
            if (fetchSelected && selectedWatchlistIdRef.current) {
                try {
                    const currentwatchList = await getWatchlist({ watchlistId: selectedWatchlistIdRef.current })
                    const index = data.watchlists.findIndex((w) => w.id === selectedWatchlistIdRef.current)
                    if (index !== -1) {
                        data.watchlists[index] = currentwatchList
                    } else {
                        data.watchlists.push(currentwatchList)
                    }
                } catch (err) {
                    console.error('Watchlist not found, switching to default')
                }
            }
            if (!response) {
                console.log('error fetching config data')
                dataUpdating.current = false
            } else {
                let defaultWatchlistIndex = -1
                const newwatchlists: Record<string, Watchlist> = fetchList ? {} : { ...watchlists }
                const validWatchlists = data.watchlists.filter((w) => !(Array.isArray(w.type) ? w.type.includes(Watchlist_Type.REFERENCE) : w.type === Watchlist_Type.REFERENCE) || w.shareId)
                data.watchlists.forEach((userWatchlist) => {
                    const filterUserWatchlist: Watchlist = {
                        ...userWatchlist,
                        type: Array.isArray(userWatchlist.type) ? userWatchlist.type : [userWatchlist.type],
                        watchlistItems: userWatchlist.watchlistItems
                            .map((watchlistItem) => {
                                let itemId = ''
                                const { type, security, securityExpression, sectionName } = watchlistItem
                                if (type === Watchlist_WatchlistItem_Type.SECURITY && security) {
                                    const securityKey = getSecurityKey(security.exchange, security.code)
                                    itemId = securityKey
                                } else if (type === Watchlist_WatchlistItem_Type.EXPRESSION && securityExpression) {
                                    itemId = securityExpression!.expression
                                } else if (type === Watchlist_WatchlistItem_Type.SECTION && sectionName !== undefined) {
                                    itemId = sectionName
                                }

                                return {
                                    id: itemId,
                                    item: watchlistItem
                                }
                            })
                            .filter((watchlistItem) => {
                                const { type, security, securityExpression, sectionName } = watchlistItem.item
                                return (
                                    watchlistItem.id &&
                                    ((type === Watchlist_WatchlistItem_Type.SECURITY && security) ||
                                        (type === Watchlist_WatchlistItem_Type.EXPRESSION && securityExpression && !securityExpression.expired && securityExpression.securities.length > 0) ||
                                        (type === Watchlist_WatchlistItem_Type.SECTION && sectionName !== undefined))
                                )
                            })
                    }
                    newwatchlists[userWatchlist.id] = filterUserWatchlist
                })
                if (!validWatchlists.some((w) => w.id === selectedWatchlistIdRef.current)) {
                    selectedWatchlistIdRef.current = validWatchlists[0]?.id
                }
                dispatch(setWatchlists(newwatchlists))
                if (validWatchlists.length < 1) {
                    commonActions.dispatchWatchlistId(dispatch, undefined)
                } else if (setDefault || !validWatchlists.map((w) => w.id).includes(selectedWatchlistId)) {
                    if (defaultWatchlistIndex !== -1) {
                        commonActions.dispatchWatchlistId(dispatch, validWatchlists[defaultWatchlistIndex].id)
                    } else {
                        commonActions.dispatchWatchlistId(dispatch, validWatchlists[0].id)
                    }
                }
                dataUpdating.current = false
            }
            hasInitialLoaded.current = true
            setLoading(false)
        },
        [selectedWatchlistId, watchlists]
    )

    const handleDelete = async () => {
        if (contextWatchlistId) {
            const watchlistId = contextWatchlistId
            if (watchlistId) {
                try {
                    await deleteWatchlist({ dispatch, watchlistId })
                    await updateWatchlist(false, true, true)
                } catch (err) {
                    console.error('Error deleting watchlist:', err)
                }
            }
        }
        handleCloseContextMenu()
    }

    useEffect(() => {
        ;(async () => {
            // await fetchCommunityWatchList({ dispatch })
            await updateWatchlist(false, true, true)
        })()
    }, [])

    const onWatchlistChanged = useCallback(
        async (id: string) => {
            selectedWatchlistIdRef.current = id
            await updateWatchlist(false, true)
            commonActions.dispatchWatchlistId(dispatch, id)
        },
        [updateWatchlist]
    )

    const onWatchlistSelected = useCallback(
        (watchlistId: string) => {
            if(!communityWatchlistAccess && watchlists[watchlistId]?.type.includes(Watchlist_Type.REFERENCE)) {
                dispatch(setFeatureRestrictionDialog({
                    open: true,
                    feature: "Community watchlists",
                    featureRestrictionCode:FeatureCode.COMMUNITY_WATCHLIST_ACCESS

                }))
                return
            }
            if (selectedWatchlistId !== watchlistId) {
                onWatchlistChanged(watchlistId)
            }
        },
        [selectedWatchlistId, onWatchlistChanged, communityWatchlistAccess, watchlists]
    )

    useEffect(() => {
        const securityKeys: Set<string> = new Set()
        const selectedWatchlist = watchlists[selectedWatchlistId]
        // Object.values(watchlists).forEach((wl) => {
        if (selectedWatchlist) {
            selectedWatchlist.watchlistItems.forEach((wlItem) => {
                if (wlItem?.item?.type === Watchlist_WatchlistItem_Type.SECURITY && wlItem.item.security) {
                    securityKeys.add(getSecurityKey(wlItem.item.security.exchange, wlItem.item.security.code))
                } else if (wlItem?.item?.type === Watchlist_WatchlistItem_Type.EXPRESSION && wlItem?.item?.securityExpression) {
                    wlItem.item.securityExpression!.securities.forEach((security: Security) => {
                        securityKeys.add(getSecurityKey(security.exchange, security.code))
                    })
                } else {
                    return
                }
            })
        }
        // })
        websocketActions.setWebsocketSubscribeList(dispatch, {
            securitySnapshotSubscriptions: [
                {
                    tag: subscriptionTags.WATCHLIST,
                    securities: Array.from(securityKeys).map((securityKey) => {
                        const [exc, code] = getExchangeAndCodeFromKey(securityKey)
                        return {
                            exchange: exc,
                            code: code
                        }
                    })
                }
            ]
        })
        const newfetchedSnapshots = Array.from(securityKeys).join('-')
        if (securityKeys.size && lastfetchedSnapshots.current !== newfetchedSnapshots) {
            lastfetchedSnapshots.current = newfetchedSnapshots
            fetchSecuritySnapshotsBulk({
                securities: Array.from(securityKeys)
                    .map((securityKey: string) => {
                        const [exc, code] = getExchangeAndCodeFromKey(securityKey)
                        return {
                            exchange: exc,
                            code: code
                        }
                    })
                    .filter((sec) => sec !== undefined),
                dispatch: dispatch
            }).catch((error) => {
                console.error('Error fetching security data', error)
                return
            })
        }
    }, [watchlists, selectedWatchlistId])

    // Subscribe to watchlist updates when selected watchlist changes
    useEffect(() => {
        if (selectedWatchlist != null) {
            const watchlistIdToSubscribe = selectedWatchlist.shareId || selectedWatchlist.id

            // Only subscribe if we're not already subscribed to this watchlist
            if (currentSubscribedWatchlistRef.current !== watchlistIdToSubscribe) {
                // Unsubscribe from previous watchlist if any
                if (currentSubscribedWatchlistRef.current) {
                    websocketActions.setWebsocketSubscribeList(dispatch, {
                        watchlistSubscriptions: []
                    })
                }

                // Subscribe to the new watchlist
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    watchlistSubscriptions: [
                        {
                            tag: 'WATCHLIST',
                            WatchlistIds: [watchlistIdToSubscribe]
                        }
                    ]
                })

                currentSubscribedWatchlistRef.current = watchlistIdToSubscribe
                console.log('Subscribed to watchlist:', watchlistIdToSubscribe)
            }
        } else {
            // Unsubscribe if no watchlist is selected
            if (currentSubscribedWatchlistRef.current) {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    watchlistSubscriptions: []
                })
                currentSubscribedWatchlistRef.current = null
                console.log('Unsubscribed from watchlist')
            }
        }
    }, [selectedWatchlist, dispatch])

    // Cleanup subscription on unmount
    useEffect(() => {
        return () => {
            if (currentSubscribedWatchlistRef.current) {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    watchlistSubscriptions: []
                })
            }
        }
    }, [dispatch])

    const addOrRemoveWatchlistItem = useCallback(
        async (watchlistItem: Watchlist_WatchlistItem, addToWatchlist: boolean, watchlistId: string) => {
            try {
                if (watchlistItem) {
                    await changeWatchlistSecurities({
                        watchlistItem: addToWatchlist ? watchlistItem : { watchlistItems: [watchlistItem] },
                        watchlistId: watchlistId,
                        method: addToWatchlist ? 'POST' : 'DELETE'
                    })
                    await updateWatchlist(false, true)
                }
            } catch (err: any) {
                const { errorCode, message } = parseErrorFromArrayBuffer(err)
                if (errorCode === ErrorCodes.VALIDATION_ERROR) {
                    commonActions.setApiError(dispatch, { error: true, errorType: 'warning', message: message ?? "Expired security can't be added to watchlist." })
                } else {
                    console.error('Error changing watchlist securities', err)
                }
            }
        },
        [dispatch, updateWatchlist]
    )

    useEffect(() => {
        if (addOrRemoveWatchlistDataItemData && !addOrRemoveWatchlistDataItemData.showHighlighted) {
            addOrRemoveWatchlistItem(addOrRemoveWatchlistDataItemData.watchlistItem, addOrRemoveWatchlistDataItemData.selected, addOrRemoveWatchlistDataItemData.watchlistId)
        }
    }, [addOrRemoveWatchlistDataItemData])

    const onCreateNewWatchlistClicked = useCallback(
        async (buttonNumber?: number, shareType: Watchlist_ShareType = Watchlist_ShareType.USER, type: Watchlist_Type[] = [Watchlist_Type.LIST]) => {
            try {
                const existingNames = new Set(userWatchlists.map((wl) => (wl.name || '').trim().toLowerCase()))

                const baseName = ''
                let generatedName = baseName

                // if (existingNames.has(baseName.toLowerCase())) {
                let index = 1
                while (existingNames.has(`${index}`.toLowerCase())) {
                    index++
                }
                generatedName = `${index}`
                // }

                const randomNumber = Math.floor(Math.random() * 10000)
                const watchlistId = 'wl' + randomNumber

                let shareId = ''
                if (shareType === Watchlist_ShareType.COMMUNITY || shareType === Watchlist_ShareType.SHARED) {
                    shareId = uuid()
                }

                const createdWatchlist = await createWatchlist({
                    body: {
                        id: watchlistId,
                        name: generatedName,
                        watchlistItems: [],
                        shareType,
                        type,
                        shareId
                    }
                })
                await updateWatchlist(false, false, true)
                commonActions.dispatchWatchlistId(dispatch, createdWatchlist.id)
                scrollDivRef.current.scrollTo({
                    left: (scrollDivRef.current?.scrollWidth ?? 0) + 10,
                    behavior: 'smooth'
                })
            } catch (error) {
                console.error('Error creating watchlist', error)
            }
        },
        [userWatchlists, dispatch, updateWatchlist]
    )

    const handleRenameDoubleClick = (watchlistId: string, watchlistName: string) => {
        if (watchlistId) {
            setCurrentRenameWatchlist({ id: watchlistId, name: watchlistName })
            setRenameDialogOpen(true)
        }
    }

    useEffect(() => {
        const el = scrollDivRef.current
        if (!el) return
        const checkScroll = () => {
            setCanScrollLeft(el.scrollLeft > 0)
            setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth)
        }
        checkScroll()
        el.addEventListener('scroll', checkScroll)
        window.addEventListener('resize', checkScroll)

        return () => {
            el.removeEventListener('scroll', checkScroll)
            window.removeEventListener('resize', checkScroll)
        }
    }, [userWatchlists])

    const onRenamed = useCallback(
        async (name: string) => {
            try {
                await renameWatchlist({
                    watchlistId: selectedWatchlistId,
                    name: name,
                    body: {}
                })
                await updateWatchlist(false, true)
            } catch (error) {
                console.error('something went wrong in renaming watchlist', error)
            }
        },
        [selectedWatchlistId, updateWatchlist]
    )
    const onWatchListDrop = useCallback(async () => {
        if (draggedIndex === null || draggedOverItemIndex === null) return

        const updated = [...userWatchlists]
        const [draggedItem] = updated.splice(draggedIndex as any, 1)
        updated.splice(draggedOverItemIndex, 0, draggedItem)
        const reorderedWatchlists = updated.map((watchlist, index) => ({
            ...watchlist,
            priority: index
        }))

        const reorderedWatchlistsById = reorderedWatchlists.reduce<Record<string, Watchlist>>((acc, watchlist) => {
            acc[watchlist.id] = watchlist
            return acc
        }, {})

        dispatch(setWatchlists(reorderedWatchlistsById))

        try {
            await reorderWatchlist({
                watchlistId: selectedWatchlistId,
                body: {
                    watchlists: reorderedWatchlists
                        .sort((a, b) => a.priority - b.priority)
                        .map((w) => ({
                            id: w.id,
                            priority: w.priority
                        }))
                }
            })
        } catch (error) {
            console.error('Something went wrong', error)
        }
        setDraggedOverItemIndex(null)
    }, [draggedIndex, draggedOverItemIndex, dispatch, selectedWatchlistId, setDraggedOverItemIndex, userWatchlists])

    const onWatchListItemDrop = useCallback(
        async (draggedItems: WatchlistItemData[], draggedOverItem: WatchlistItemData | undefined) => {
            if (!selectedWatchlist) return
            const itemsCopy = [...selectedWatchlist.watchlistItems]
            const draggedIds = new Set(draggedItems.map((item) => item.id))
            const filteredItems = itemsCopy.filter((item) => !draggedIds.has(item.id))
            let toIndex = draggedOverItem ? filteredItems.findIndex((item) => item.id === draggedOverItem.id) : filteredItems.length
            if (toIndex < 0) toIndex = filteredItems.length
            const draggedItemsSorted = [...draggedItems].sort((a, b) => {
                return itemsCopy.findIndex((item) => item.id === a.id) - itemsCopy.findIndex((item) => item.id === b.id)
            })
            filteredItems.splice(toIndex, 0, ...draggedItemsSorted)
            const updatedItemsCopy = filteredItems.map((item, index) => ({
                ...item,
                item: {
                    ...item.item,
                    priority: index
                }
            }))
            const updatedWatchlist = selectedWatchlist.watchlistItems
                .map((watchlistItem) => {
                    const updated = updatedItemsCopy.find((i) => i.id === watchlistItem.id)
                    return updated ? { ...updated, priority: updated.item.priority } : watchlistItem
                })
                .sort((a, b) => a.item.priority - b.item.priority)

            const updatedWatchlistObject: ReorderWatchlistRequest = {
                watchlistItems: updatedWatchlist.map((v) => v.item)
            }

            dispatch(
                setWatchlists({
                    ...watchlists,
                    [selectedWatchlistId]: {
                        ...selectedWatchlist,
                        watchlistItems: [...updatedWatchlist]
                    }
                })
            )

            try {
                await reorderWatchlistItem({
                    watchlistId: selectedWatchlistId,
                    body: updatedWatchlistObject
                })
                await updateWatchlist(false, true)
            } catch (error) {
                console.error('Something went wrong', error)
            }
        },
        [dispatch, selectedWatchlist, selectedWatchlistId, watchlists, updateWatchlist]
    )

    const handleChange = useCallback(
        (_event: React.MouseEvent<HTMLElement>, watchlistId: string | null) => {
            if (watchlistId && watchlistId !== selectedWatchlistId) {
                onWatchlistSelected(watchlistId)
            }
        },
        [onWatchlistSelected, selectedWatchlistId]
    )

    const onEditClicked = useCallback((renameWatchlist: boolean) => {
        setShowBottomBar(!renameWatchlist)
    }, [])

    const setModalOpen = useCallback((open: boolean) => {
        setSecuritiesModalOpen(open)
    }, [])

    const showInitialShimmer = loading && !hasInitialLoaded.current

    if (showInitialShimmer) {
        return (
            <Box className={classes.paperRoot} sx={{ position: 'relative', overflow: 'hidden', flex: 1, minHeight: 0 }}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <SubtleShimmer loading fill>
                        <WatchlistSkeleton tableView={tableView} />
                    </SubtleShimmer>
                </Box>
            </Box>
        )
    }

    return (
        <Box className={`${classes.paperRoot} ${loading ? classes.blurred : classes.clear}`} sx={{ flex: 1, minHeight: 0 }}>
            <>
                <WatchListTopBar
                    onRenamed={onRenamed}
                    onWatchlistSelected={onWatchlistSelected}
                    onWatchlistSecuritiesChanged={addOrRemoveWatchlistItem}
                    modalOpen={securitiesModalOpen}
                    setIsModalOpened={setModalOpen}
                    onEditClicked={onEditClicked}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    communityWatchlistAccess={communityWatchlistAccess}
                />
                <WatchListData
                    addOrRemoveWatchlistItem={addOrRemoveWatchlistItem}
                    setModalOpen={setModalOpen}
                    onDrop={onWatchListItemDrop}
                    editMode={editMode}
                />
                {showBottomBar &&
                    (previewId.isActive && previewId.watchlistId ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                                padding: '4px 8px',
                                gap: 1
                            }}
                        >
                            <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewId.name ?? 'Preview Watchlist'}</Box>

                            <Tooltip title="Add to My Watchlists" placement="top" arrow>
                                <IconButton
                                    size="small"
                                    onClick={async (e) => {
                                        e.stopPropagation()
                                        try {
                                            dispatch(clearPreviewWatchlist())
                                            const newWatchList = await addCommunityToWatchList({
                                                shareId: previewWatchlist.shareId,
                                                body: {}
                                            })
                                            await updateWatchlistFromUtils(dispatch, newWatchList.id, dataUpdating, setLoading, false, true, true)
                                            updateWatchlist(false, true, true)
                                        } catch (error) {
                                            console.error('Failed to add preview watchlist:', error)
                                        }
                                    }}
                                >
                                    <PlusIcon width={16} height={16} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Close Preview" placement="top" arrow>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        dispatch(clearPreviewWatchlist())
                                    }}
                                >
                                    <ClearIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                            </Tooltip>

                            <Button
                                value={undefined}
                                onClick={async (e) => {
                                    console.log('search button clicked')
                                    e.stopPropagation()
                                    const response = await fetchCommunityWatchList({ dispatch })
                                    if (!response || !Array.isArray(response.watchlists)) {
                                        console.error('No watchlists found')
                                        setSearchDialogOpen(true)
                                        return
                                    }
                                    setSearchDialogOpen(true)
                                }}
                                size="small"
                                sx={{
                                    maxWidth: '64px',
                                    minWidth: '40px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '34px'
                                }}
                            >
                                <SearchIcon />
                            </Button>
                            <WatchlistSearchDialog open={isSearchDialogOpen} onClose={() => setSearchDialogOpen(false)} watchlists={watchlists} onWatchlistSelected={onWatchlistSelected} communityWatchlistAccess={communityWatchlistAccess} />
                        </Box>
                    ) : (
                        <StyledToggleButtonGroup
                            exclusive
                            onChange={handleChange}
                            sx={{
                                display: 'flex',
                                width: '100%',
                                overflow: 'hidden',
                                alignItems: 'center',
                                position: 'relative',
                                '&:hover .scroll-buttons': {
                                    opacity: 4
                                },
                                borderTop: (theme) => `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'hidden'
                                }}
                            >
                                {canScrollLeft && (
                                    <Button
                                        className="scroll-buttons"
                                        onClick={scrollLeft}
                                        sx={{
                                            ...scrollButtonStyles,
                                            left: 0,
                                            backdropFilter: 'blur(1px)'
                                        }}
                                    >
                                        <ThinArrowLeftIcon fontSize="medium" />
                                    </Button>
                                )}
                                <Box
                                    ref={scrollDivRef}
                                    sx={{
                                        display: 'flex',
                                        overflowX: 'auto',
                                        flex: 1,
                                        paddingTop: '2px',
                                        scrollbarWidth: 'none',
                                        '&::-webkit-scrollbar': {
                                            display: 'none'
                                        },
                                        '&:hover .scroll-buttons': {
                                            opacity: '1 !important'
                                        }
                                    }}
                                >
                                    {userWatchlists
                                        .filter((watchlist) => !(!watchlist.shareId && watchlist?.type?.includes(Watchlist_Type.REFERENCE)))
                                        .map((watchlist, index) => {
                                            const isSelected = selectedWatchlistId === watchlist.id
                                            return (
                                                <React.Fragment key={watchlist.id}>
                                                    <Tooltip
                                                        title={
                                                            <Box display={'flex'} sx={{ alignItems: 'center', gap: 0.5 }}>
                                                                {watchlist.name}
                                                                {watchlist.shareId ? <LockIcon sx={{ fontSize: '10px' }} /> : null}
                                                            </Box>
                                                        }
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <ToggleButton
                                                            value={watchlist.id}
                                                            selected={isSelected}
                                                            size="small"
                                                            onContextMenu={(e) => handleRightClick(e, watchlist.id)}
                                                            onDoubleClick={() => {
                                                                if(!isUserAdmin && watchlist.type.includes(Watchlist_Type.REFERENCE)){
                                                                    return 
                                                                }
                                                                handleRenameDoubleClick(watchlist.id, watchlist.name)
                                                                setRenameDialogOpen(true)
                                                            }}
                                                            draggable
                                                            onDragStart={(e) => handleDragStart(index)}
                                                            onDragEnter={(e) => {
                                                                rectCache.current = e.currentTarget.getBoundingClientRect()
                                                            }}
                                                            onDrop={onWatchListDrop}
                                                            onDragOver={(e) => {
                                                                if (!rectCache) return
                                                                const centerX = (rectCache.current?.left ?? 0) + (rectCache.current?.width ?? 0) / 2

                                                                if (e.clientX < centerX) {
                                                                    handleDragOver(e as any, index)
                                                                } else {
                                                                    handleDragOver(e as any, index + 1)
                                                                }
                                                                e.preventDefault()
                                                            }}
                                                            onDragEnd={(e) => {
                                                                setDraggedIndex(null)
                                                                setDraggedOverItemIndex(null)
                                                            }}
                                                            sx={{
                                                                minWidth: '48px',
                                                                overflow: 'hidden',
                                                                maxHeight: '36px',
                                                                border: 'none',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '5px'
                                                                // borderRight: draggedOverItemIndex === index ? '2px solid #1976d2' : 'none',
                                                            }}
                                                        >
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    width: '100%',
                                                                    textAlign: 'center'
                                                                }}
                                                            >
                                                                <Box>{watchlist.name}</Box>
                                                            </Box>
                                                        </ToggleButton>
                                                    </Tooltip>
                                                </React.Fragment>
                                            )
                                        })}
                                    {currentRenameWatchlist && (
                                        <RenameWatchlistDailog
                                            open={renameDialogOpen}
                                            currentName={currentRenameWatchlist.name}
                                            title="Rename Watchlist"
                                            subTitle="Enter a new name for your watchlist"
                                            onClose={() => {
                                                setRenameDialogOpen(false)
                                                setCurrentRenameWatchlist(null)
                                            }}
                                            onSubmit={async (newName) => {
                                                try {
                                                    await renameWatchlist({
                                                        watchlistId: currentRenameWatchlist.id,
                                                        name: newName,
                                                        body: {}
                                                    })
                                                    await updateWatchlist(false, true)
                                                } catch (error) {
                                                    console.error('something went wrong in renaming watchlist', error)
                                                }
                                                setRenameDialogOpen(false)
                                                setCurrentRenameWatchlist(null)
                                            }}
                                        />
                                    )}
                                </Box>
                                {canScrollRight && (
                                    <Button
                                        className="scroll-buttons"
                                        onClick={scrollRight}
                                        sx={{
                                            ...scrollButtonStyles,
                                            right: 0,
                                            backdropFilter: 'blur(1px)'
                                        }}
                                    >
                                        <ThinArrowRightIcon fontSize={'medium'} />
                                    </Button>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', padding: '2px', borderLeft: (theme) => `1px solid ${theme.palette.divider}` }}>
                                <Menu
                                    anchorEl={contextMenuAnchorEl}
                                    open={Boolean(contextMenuAnchorEl)}
                                    onClose={handleCloseContextMenu}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }}
                                    style={{ marginBottom: '50px' }}
                                >
                                    {!watchlists[contextWatchlistId]?.default && <MenuItem onClick={handleDelete}>Delete</MenuItem>}
                                    {(isUserAdmin || !watchlists[contextWatchlistId]?.type.includes(Watchlist_Type.REFERENCE)) && <MenuItem
                                        onClick={() => {
                                            handleRenameDoubleClick(selectedWatchlist.id, selectedWatchlist.name)
                                            setRenameDialogOpen(true)
                                        }}
                                    >
                                        Rename
                                    </MenuItem>}
                                </Menu>
                                <Tooltip title="Add Watchlist" placement="top" arrow>
                                    <Button
                                        value={undefined}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (userWatchlists.length <= 50) {
                                                onCreateNewWatchlistClicked()
                                            }
                                        }}
                                        sx={{
                                            maxWidth: '64px',
                                            minWidth: '40px',
                                            px: 1,
                                            maxHeight: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderTop: 'none'
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Search List" placement="top" arrow>
                                    <Button
                                        value={undefined}
                                        onClick={async (e) => {
                                            e.stopPropagation()
                                            const response = await fetchCommunityWatchList({ dispatch })
                                            if (!response || !Array.isArray(response.watchlists)) {
                                                console.error('No watchlists found')
                                                setSearchDialogOpen(true)
                                                return
                                            }
                                            setSearchDialogOpen(true)
                                        }}
                                        size="small"
                                        sx={{
                                            maxWidth: '64px',
                                            minWidth: '40px',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '34px'
                                        }}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </Tooltip>
                                <WatchlistSearchDialog open={isSearchDialogOpen} onClose={() => setSearchDialogOpen(false)} watchlists={watchlists} onWatchlistSelected={onWatchlistSelected} communityWatchlistAccess={communityWatchlistAccess} />
                            </Box>
                        </StyledToggleButtonGroup>
                    ))}
            </>
        </Box>
    )
}

export default memo(Watchlists)
